// HOC
// a higher-order component is a function that takes a component and returns a new component.
// Higher order component for components that require login:

import React, { useEffect } from 'react';
import { connect, ConnectedProps, useStore } from 'react-redux';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

import { RootState, AppDispatch } from '../../reduxTypes';
import { getPrerenderUserThrows } from '../../actions/prerenderUserActions';
import SpinningWheel from '../SpinningWheel';
import { PrerenderUser } from '../../features/PrerenderUser';
import { redirectIfNeeded } from '../../library/navigation';
import DynamicStateService from '../../features/DynamicStateService';
import { SessionExpiredModal } from '~features/auth';
import { isSubscriptionCancelled } from '../../hooks/usePrerenderUser';
import { WIZARD_STEPS_PATH } from '../../features/integrationWizard/steps';

const INTEGRATION_WIZARD_PATH = '/integration-wizard';

function isSessionUnauthorizedError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 401;
}

function mapStateToProps(state: RootState) {
  return {
    kcError: state.keycloak.error,
    kcLoggedIn: state.keycloak.loggedIn,
    sessionExpired: state.keycloak.sessionExpired,
    hasSession: state.prerenderUser.hasSession,
    featureFlags: state.prerenderUser.features,
    role: state.prerenderUser.role,
    email: state.prerenderUser.email,
    chargebeePlanId: state.prerenderUser.chargebeePlanId,
    cancelledAt: state.prerenderUser.cancelledAt,
    recachingDisabled: state.prerenderUser.recachingDisabled,
    id: state.prerenderUser.id,
    companyCreationDate: state.prerenderUser.companyCreationDate,
    trackingCodeInstalled: state.prerenderUser.trackingCodeInstalled,
    onboardingSurveyCompleted: state.prerenderUser.onboardingSurveyCompleted,
  };
}

// provide access to selected actions:
function mapDispatchToProps(dispatch: AppDispatch) {
  return {
    getPrerenderUserThrows: () => dispatch(getPrerenderUserThrows()),
  };
}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export type IsAuthorizedFn = (
  props: Pick<PrerenderUser, 'role' | 'email' | 'chargebeePlanId'> & {
    featureFlags: PrerenderUser['features'];
    subscriptionCancelled: boolean;
  }
) => boolean;

export default function requireAuth(
  ComposedComponent: React.ComponentType<PropsFromRedux>,
  isAuthorizedFn?: IsAuthorizedFn
) {
  const RequireAuth = (props: PropsFromRedux) => {
    const { kcError, kcLoggedIn, sessionExpired, hasSession, getPrerenderUserThrows: doGetPrerenderUserThrows } = props;
    const [initDynamicStateService, dynamicStateServiceProgress] = DynamicStateService.useInitService();

    const navigate = useNavigate();
    const location = useLocation();
    const store = useStore();

    const withSessionModal = (content: React.ReactNode) => (
      <>
        <SessionExpiredModal />
        {content}
      </>
    );

    useEffect(() => {
      // Skip the bootstrap while the session is flagged expired: the request would only
      // fail-fast (CanceledError) and leave us unauthenticated. `sessionExpired` is a
      // dependency so that when the session recovers (KEYCLOAK_AUTH_SUCCESS clears the flag
      // after a token refresh) we re-run the fetch — otherwise the app is stuck on the
      // full-page spinner forever with no retry (see PRE-2823 loader race).
      if (kcLoggedIn && !hasSession && !sessionExpired) {
        doGetPrerenderUserThrows()
          .then(() => {
            initDynamicStateService();

            const prerenderUser = store.getState().prerenderUser;
            window.saveJourney(prerenderUser.token, prerenderUser.id);
          })
          .catch((error: unknown) => {
            console.error('Unhandled auth error', error);
            const failedBecauseSessionExpired =
              store.getState().keycloak.sessionExpired || isSessionUnauthorizedError(error);
            if (failedBecauseSessionExpired) return;
            navigate('/error-uncaught');
          });
      }
    }, [kcLoggedIn, hasSession, sessionExpired]);

    if (kcError) {
      return withSessionModal(<h3>{kcError}</h3>);
    }

    if (!kcLoggedIn || !hasSession || dynamicStateServiceProgress.isFetching) {
      // Full-viewport spinner sits above page content; skip it when the session modal is open.
      return withSessionModal(sessionExpired ? null : <SpinningWheel />);
    }

    if (isAuthorizedFn) {
      const { role, email, featureFlags, chargebeePlanId, cancelledAt, recachingDisabled } = props;
      const subscriptionCancelled = isSubscriptionCancelled({ cancelledAt, recachingDisabled });
      if (!isAuthorizedFn({ featureFlags, role, email, chargebeePlanId, subscriptionCancelled })) {
        return withSessionModal(<Navigate to="/404" />);
      }
    }

    const redirectPath = redirectIfNeeded({
      id: props.id,
      companyCreationDate: props.companyCreationDate,
      trackingCodeInstalled: props.trackingCodeInstalled,
      role: props.role,
      onboardingSurveyCompleted: props.onboardingSurveyCompleted,
    });
    // On a step, useWizardSteps routes a survey-less customer itself (to the survey, or to their
    // stored step), so redirecting from one only adds a bounce that drops the link's query.
    const wizardRoutesTheCustomer =
      redirectPath?.startsWith(INTEGRATION_WIZARD_PATH) && location.pathname.startsWith(`${WIZARD_STEPS_PATH}/`);
    if (redirectPath && !location.pathname.startsWith(redirectPath) && !wizardRoutesTheCustomer) {
      return withSessionModal(<Navigate to={redirectPath} replace />);
    }

    return withSessionModal(<ComposedComponent {...props} />);
  };

  return connector(RequireAuth);
}
